import * as t from 'io-ts';
import AccountRoles from '../../enums/AccountRoles';
import { boolOrIntCodec, timestampCodec } from '../../validation/codecs';
import { enumCodec, mixed, optional, required } from '../../validation/validator';
import { CallResponseMixinSchema, CallResponseSchema } from '../common/callResponseSchema';
import { TrackingProviderRecordSchema } from '../tracking/trackingSchemas';
import { UserRecordSchema } from '../users/userSchemas';

const RoleRecordSchema = t.type({
    id: t.number,
    name: t.string,
    description: t.string,
    is_active: t.boolean,
    created_at: timestampCodec,
});
export type RoleRecord = t.TypeOf<typeof RoleRecordSchema>;

const RoleInsertSchema = mixed([
    required({
        name: t.string,
        description: t.string
    }),
    optional({
        is_active: t.boolean,
    })
]);
export type RoleInsert = t.TypeOf<typeof RoleInsertSchema>;

const PermissionRecordSchema = t.type({
    id: t.number,
    name: t.string,
    description: t.string,
    is_active: t.boolean,
    created_at: timestampCodec,
    updated_at: timestampCodec,
});
export type PermissionRecord = t.TypeOf<typeof PermissionRecordSchema>;

const AccountUserRecordSchema = t.type({
    account_id: t.number,
    user_id: t.number,
    role_id:t.number,
    created_at: timestampCodec,
    updated_at: timestampCodec,
});
export type AccountUserRecord = t.TypeOf<typeof AccountUserRecordSchema>;

const AccountUserInsertSchema = mixed([
    required({
        account_id: t.number,
        user_id: t.number,
        role_id: t.number,
        updated_at: timestampCodec,
    }),
    optional({
        created_at: timestampCodec,
    })
]);
export type AccountUserInsert = t.TypeOf<typeof AccountUserInsertSchema>;

const UserPermissionsResponseSchema = t.type({
    account_id: t.number,
    user_id: t.number,
    role: t.string,
    created_at: timestampCodec,
    updated_at: timestampCodec,
    permissions: t.array(PermissionRecordSchema)
});
export type UserPermissionsResponse = t.TypeOf<typeof UserPermissionsResponseSchema>;

export const AccountRecordSchema = t.type({
    id: t.number,
    name: t.string,
    created_at: timestampCodec,
    updated_at: timestampCodec,
    stripe_customer_id: t.union([ t.string, t.null ]),
    is_business: t.boolean,
    total_seats: t.number,
    assigned_seats: t.number,
    available_seats: t.number,
    is_demo: t.boolean,
    is_public: t.boolean
});
export type AccountRecord = t.TypeOf<typeof AccountRecordSchema>;

export const AccountInsertSchema = mixed([
    required({
        name: t.string,
        updated_at: timestampCodec,
    }),
    optional({
        stripe_customer_id: t.union([ t.string, t.null ]),
        is_business: t.boolean,
        is_demo: t.boolean,
    })
]);
export type AccountInsert = t.TypeOf<typeof AccountInsertSchema>;

export const ProductAuthorizationRecordSchema = required({
    product_id: t.number,
    authorization_id: t.number,
    created_at: timestampCodec,
    updated_at: timestampCodec
});
export type ProductAuthorizationRecord = t.TypeOf<typeof ProductAuthorizationRecordSchema>;

/**
 * Authorizations
 */
export const AuthorizationRecordSchema = t.type({
    id: t.number,
    name: t.string,
    description: t.string,
    is_active: t.boolean,
    created_at: timestampCodec,
    updated_at: timestampCodec
});
export type AuthorizationRecord = t.TypeOf<typeof AuthorizationRecordSchema>;

/**
 * Products
 */
export const ProductRecordSchema = t.type({
    id: t.number,
    external_uid: t.string,
    name: t.string,
    description: t.string,
    created_at: timestampCodec,
    updated_at: timestampCodec,
    is_active: t.boolean,
    quantity_to_seat_ratio: t.number,
    is_business: t.boolean,
});
export type ProductRecord = t.TypeOf<typeof ProductRecordSchema>;

/**
 * AccountProductRecord
 */
export const AccountProductRecordSchema = mixed([
    required({
        id: t.number,
        account_id: t.number,
        product_id: t.number,
        created_at: timestampCodec,
        updated_at: timestampCodec,
        is_active: t.boolean,
        is_paused: t.boolean
    }),
    optional({
        stripe_subscription_id: t.union([t.string, t.null]),
        seat_count: t.union([t.number, t.null]),
        end_at: t.union([timestampCodec, t.null, t.undefined]),
        start_at: t.union([timestampCodec, t.null, t.undefined]),
    })
]);
export type AccountProductRecord = t.TypeOf<typeof AccountProductRecordSchema>;

export const AccountProductInsertSchema = mixed([
    required({
        account_id: t.number,
        product_id: t.number,
        updated_at: timestampCodec,
    }),
    optional({
        stripe_subscription_id: t.union([t.string, t.null]),
        seat_count: t.union([t.number, t.null]),
        is_active: t.boolean,
        is_paused: t.boolean,
        start_at: t.union([timestampCodec, t.null, t.undefined]),
        end_at: t.union([timestampCodec, t.null, t.undefined]),
    })
]);
export type AccountProductInsert = t.TypeOf<typeof AccountProductInsertSchema>;

const GetAccountsSchema = t.partial({
    is_admin: t.boolean,
    user_id: t.number,
    user_email: t.string,
    account_ids: t.array(t.number),
    limit: t.number,
    skip: t.number,
    search_query: t.string,
    resolve_members: t.boolean,
    resolve_authorizations: t.boolean,
    resolve_products: t.boolean,
});

const AccountResponseSchema = t.intersection([
    AccountRecordSchema, 
    t.type({ 
        members: t.array(AccountUserRecordSchema),
        products: t.array(AccountProductRecordSchema),
        authorizations: t.array(AuthorizationRecordSchema),
        total_seats: t.number,
        assigned_seats: t.number,
        available_seats: t.number
    }),
    t.partial({
        member_records: t.array(UserRecordSchema),
        product_records: t.array(ProductRecordSchema),
        tracking_providers: t.array(TrackingProviderRecordSchema),
    }),
]);
export type GetAccountResponse = t.TypeOf<typeof AccountResponseSchema>;

const AccountsResponseSchema = t.type({
    accounts: t.array(AccountResponseSchema),
    total_count: t.number
}); 
export type GetAccountsResponse = t.TypeOf<typeof AccountsResponseSchema>;

export const AuthorizationInsertSchema = mixed([
    required({
        name: t.string,
        description: t.string,
        updated_at: timestampCodec,
    }),
    optional({
        is_active: t.boolean,
    })
]);
export type AuthorizationInsert = t.TypeOf<typeof AuthorizationInsertSchema>;

// Add/Remove/Update Member
export const UpdateAccount = t.type({
    account_id: t.number,
    user_id: t.number,
    role: enumCodec(AccountRoles),
});

// Add/Remove/Update Member
export const UpdateMemberSchema = mixed([
    required({
        action: t.union([t.literal('add'), t.literal('remove'), t.literal('update')]),
        account_id: t.number,
        user_id: t.number,
        role_id: t.number,
    }),
    optional({
        user_email: t.string
    })
]);

// Add/Remove/Update Authorization
export const UpdateAuthorizationSchema = mixed([
    required({
        action: t.union([t.literal('add'), t.literal('remove'), t.literal('update')]),
    }),
    optional({
        authorization_id: t.number,
        name: t.string,
        description: t.string,
        is_active: t.boolean,
    })
]);

// Get User Permissions
export const GetUserPermissionsSchema = t.type({
    user_id: t.number,
    account_id: t.number,
});

// Create Account
export const CreateAccountSchema = mixed([
    required({
        name: t.string,
    }),
    optional({
        stripe_customer_id: t.union([t.string, t.null]),
        is_business: boolOrIntCodec,
        is_demo: boolOrIntCodec,
    })
]);
export type CreateAccountRequest = t.TypeOf<typeof CreateAccountSchema>;

export const RecoverAccountSchema = mixed([
    required({
        user_id: t.number,
    }),
    optional({
        stripe_customer_id: t.string,
        account_id: t.number,
    }),
]);
export type RecoverAccountRequest = t.TypeOf<typeof RecoverAccountSchema>;

export const RecoverAccountResponseSchema = t.type({
    recovery_type: t.union([t.literal("stripe_customer_id"), t.literal("account_id")]),
    user_id: t.number,
    account_id: t.union([t.number, t.null]),
    stripe_customer_id: t.string,
    processed_subscriptions: t.number,
    subscription_ids: t.array(t.string),
});
export type RecoverAccountResponse = t.TypeOf<typeof RecoverAccountResponseSchema>;

export const SearchUsersSchema = mixed([
    required({
        search_query: t.string,
    }),
    optional({
        is_admin: t.boolean,
        limit: t.union([t.number, t.undefined]),
        skip: t.union([t.number, t.undefined]),
        include_accounts: t.union([t.array(t.number), t.null]),
        exclude_accounts: t.union([t.array(t.number), t.null]),
        user_ids: t.union([t.array(t.number), t.null])
    })
]);
export type SearchUsersRequest = t.TypeOf<typeof SearchUsersSchema>;

// Add/Remove/Update Authorization
export const UpdateProductSchema = mixed([
    required({
        action: t.union([t.literal('add'), t.literal('remove'), t.literal('update')]),
        account_id: t.number,
        product_id: t.number,
    }),
    optional({
        stripe_subscription_id: t.union([t.string, t.null]),
        seat_count: t.union([t.number, t.null]),
        is_active: t.boolean,
        is_paused: t.boolean,
        start_at: t.union([timestampCodec, t.null, t.undefined]),
        end_at: t.union([timestampCodec, t.null, t.undefined]),
    })
]);

export const UpdateAccountSchema = mixed([
    required({
        account_id: t.number,
    }),
    optional({
        name: t.string,
        stripe_customer_id: t.union([t.string, t.null]),
        is_business: boolOrIntCodec,
    })
]);

// Account Team Record
export const AccountTeamRecordSchema = t.type({
    account_id: t.number,
    team_id: t.number,
    created_at: timestampCodec,
    updated_at: timestampCodec
});
export type AccountTeamRecord = t.TypeOf<typeof AccountTeamRecordSchema>;

// Add/Remove Team
export const UpdateAccountTeamSchema = t.type({
    action: t.union([t.literal('add'), t.literal('remove')]),
    account_id: t.number,
    team_id: t.number
});
export type UpdateAccountTeamRequest = t.TypeOf<typeof UpdateAccountTeamSchema>;

// Add/Remove/Update Team
export const UpdateTeamRequestSchema = t.intersection([
    t.type({
        action: t.union([t.literal('add'), t.literal('update'), t.literal('remove')]),
        team_id: t.number
    }),
    t.partial({
        account_id: t.number, // Optional account_id for team-account association
        name: t.union([t.string, t.null]),
        group: t.union([t.string, t.null]),
        doc_id: t.union([t.string, t.null]),
        license_provider_id: t.union([t.number, t.null]),
        club_id: t.union([t.number, t.null])
    })
]);
export type UpdateTeamRequest = t.TypeOf<typeof UpdateTeamRequestSchema>;

// Search Accounts
export const SearchAccountsSchema = mixed([
    required({
        search_query: t.string,
    }),
    optional({
        filters: t.type({
            is_business: t.boolean,
            has_products: t.union([t.literal('with'), t.literal('without')]),
            min_members: t.number,
        }),
        limit: t.number,
        skip: t.number,
        resolve_members: t.boolean,
        resolve_products: t.boolean,
        resolve_authorizations: t.boolean,
        include_accounts: t.array(t.number),
        exclude_accounts: t.array(t.number)
    })
]);
export type SearchAccountsRequest = t.TypeOf<typeof SearchAccountsSchema>;

export const AccountReportRequestSchema = t.intersection([
    t.type({
        account_id: t.number,
    }),
    t.partial({
        from_date: t.union([timestampCodec, t.null]),
        to_date: t.union([timestampCodec, t.null])
    })
]);

export const AccountReportResultSchema = t.type({
    user_doc_id: t.string,
    user_created_at: t.union([timestampCodec, t.null]),
    user_id: t.number,
    last_active_at: t.union([timestampCodec, t.null]),
    gameplay_duration: t.number,
    total_score: t.union([t.number, t.null]),
    scan_rate: t.union([t.number, t.null]),
    scan_timing: t.union([t.number, t.null]),
    critical_scan_score: t.union([t.number, t.null]),
    decision_score: t.union([t.number, t.null]),
    awareness_score: t.union([t.number, t.null]),
    packing_rate: t.union([t.number, t.null]),
    user_first_name: t.string,
    user_last_name: t.string,
    previous_gameplay_duration: t.union([t.number, t.null]),
    previous_total_score: t.union([t.number, t.null]),
    previous_scan_rate: t.union([t.number, t.null]),
    previous_scan_timing: t.union([t.number, t.null]),
    previous_critical_scan_score: t.union([t.number, t.null]),
    previous_decision_score: t.union([t.number, t.null]),
    previous_awareness_score: t.union([t.number, t.null]),
    previous_packing_rate: t.union([t.number, t.null])
});

export const AccountReportResponseSchema = t.type({
    overview: t.array(AccountReportResultSchema),
    users: t.any // This matches the getUserReport response type
});

// Create Demo User And Account
export const CreateDemoUserAndAccountSchema = mixed([
    required({
        first: t.string,
        last: t.string,
        email: t.string,
    }),
    optional({
        pass: t.string,
        dob: t.string,
        uid: t.string,
        referral: t.union([t.string, t.null]),
        expiry_days: t.number,
        user_rating: t.number,
        email_verification_return_url: t.union([t.string, t.null]),
    }),
]);
export type CreateDemoUserAndAccountRequest = t.TypeOf<typeof CreateDemoUserAndAccountSchema>;

// End Demo Account
export const EndDemoAccountSchema = t.type({
    user_id: t.number,
    account_id: t.number,
});
export type EndDemoAccountRequest = t.TypeOf<typeof EndDemoAccountSchema>;

// Convert Existing User to Demo
export const ConvertUserToDemoSchema = mixed([
    required({
        account_id: t.number,
    }),
    optional({
        expiry_days: t.number,
    })
]);
export type ConvertUserToDemoRequest = t.TypeOf<typeof ConvertUserToDemoSchema>;

// Create Demo Account for Existing User
export const CreateDemoAccountSchema = mixed([
    required({}),
    optional({
        account_name: t.string,
        expiry_days: t.number,
    })
]);
export type CreateDemoAccountRequest = t.TypeOf<typeof CreateDemoAccountSchema>;

// Client API
export const AccountApis = {
    Account: {
        CreateAccount: {
            Name: 'account-createAccount',
            Request: CreateAccountSchema,
            Response: mixed([required({ data: t.type({ id: t.number }) }), CallResponseMixinSchema])
        },
        RecoverAccount: {
            Name: 'account-recovery-recoverAccount',
            Request: RecoverAccountSchema,
            Response: mixed([
                required({
                    data: RecoverAccountResponseSchema
                }),
                CallResponseMixinSchema
            ])
        },
        GetAccounts: {
            Name: 'account-temp-getAccounts',
            Request: GetAccountsSchema,
            Response: mixed([
                required({ 
                    data: t.array(AccountResponseSchema)
                }), 
                CallResponseMixinSchema
            ]),
        },
        GetUserPermissions: {
            Name: 'account-getUserPermissions',
            Request: GetUserPermissionsSchema,
            Response: mixed([required({ data: UserPermissionsResponseSchema }), CallResponseMixinSchema]),
        },
        UpdateMember: {
            Name: 'account-updateMember',
            Request: UpdateMemberSchema,
            Response: CallResponseSchema
        },
        UpdateAccountTeam: {
            Name: 'account-updateAccountTeam',
            Request: UpdateAccountTeamSchema,
            Response: CallResponseSchema
        },
        UpdateTeam: {
            Name: 'account-updateTeam',
            Request: UpdateTeamRequestSchema,
            Response: CallResponseSchema
        },
        UpdateProduct: {
            Name: 'account-updateProduct',
            Request: UpdateProductSchema,
            Response: CallResponseSchema
        },
        UpdateAccount: {
            Name: 'account-updateAccount',
            Request: UpdateAccountSchema,
            Response: CallResponseSchema
        },
        UpdateAuthorization: {
            Name: 'account-updateAuthorization',
            Request: UpdateAuthorizationSchema,
            Response: CallResponseSchema
        },
        GetRoles: {
            Name: 'account-getRoles',   
            Request: t.partial({
                include_inactive: t.boolean,
            }),
            Response: mixed([required({ data: t.array(RoleRecordSchema) }), CallResponseMixinSchema])
        },
        GetProducts: {
            Name: 'account-getProducts',
            Request: t.type({}),
            Response: mixed([required({ data: t.array(ProductRecordSchema) }), CallResponseMixinSchema])
        },
        GetAuthorizations: {
            Name: 'account-getAuthorizations',
            Request: t.type({}),
            Response: mixed([
                required({ 
                    data: t.array(t.type({
                        id: t.number,
                        name: t.string,
                        description: t.string,
                        is_active: t.boolean,
                        created_at: timestampCodec,
                        updated_at: timestampCodec
                    }))
                }), 
                CallResponseMixinSchema
            ])
        },
        SearchUsers: {
            Name: 'account-searchUsers',
            Request: SearchUsersSchema,
            Response: mixed([
                required({ 
                    data: t.array(UserRecordSchema)
                }), 
                CallResponseMixinSchema
            ])
        },
        SearchAccounts: {
            Name: 'account-searchAccounts',
            Request: SearchAccountsSchema,
            Response: mixed([
                required({ 
                    data: t.array(AccountResponseSchema),
                    total_count: t.number
                }), 
                CallResponseMixinSchema
            ])
        },
        RefreshAccess: {
            Name: 'account-refreshAccess',
            Request: t.type({
                account_id: t.number
            }),
            Response: CallResponseSchema
        },
        CreateDemoUserAndAccount: {
            Name: 'account-createDemoUserAndAccount',
            Request: CreateDemoUserAndAccountSchema,
            Response: CallResponseSchema
        },
        EndDemoAccount: {
            Name: 'account-endDemoAccount',
            Request: EndDemoAccountSchema,
            Response: CallResponseSchema
        },
        ConvertUserToDemo: {
            Name: 'account-convertUserToDemo',
            Request: ConvertUserToDemoSchema,
            Response: CallResponseSchema
        },
        CreateDemoAccount: {
            Name: 'account-createDemoAccount',
            Request: CreateDemoAccountSchema,
            Response: CallResponseSchema
        }
    }
}